/**
 * Captures the current frame of the AVP 3D stream as a JPEG blob.
 * Accesses the <video> element inside avp-3dws-deck's shadow DOM and draws
 * it onto a canvas to produce a JPEG image.
 *
 * NOTE: Canvas taint may occur if the streaming server does not send
 * Access-Control-Allow-Origin headers. The caller should handle SecurityError.
 */
export async function captureShowroomFrame(showroomEl: HTMLElement, quality = 0.92): Promise<Blob> {
  const shadowRoot = showroomEl.shadowRoot;
  if (!shadowRoot) {
    throw new Error('No shadow root on showroom element');
  }

  const video = shadowRoot.querySelector('video');
  if (!video) {
    throw new Error('No video element in showroom shadow DOM');
  }

  if (video.videoWidth === 0 || video.videoHeight === 0) {
    throw new Error('Video dimensions are zero — stream may not be ready');
  }

  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get 2D context from canvas');
  }

  ctx.drawImage(video, 0, 0);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('canvas.toBlob returned null'));
        }
      },
      'image/jpeg',
      quality
    );
  });
}
